import { useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  XMarkIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Header } from '@/components/Layout';
import { Modal, ConfirmDialog, PageLoader, EmptyState } from '@/components/common';
import { usersApi, rolesApi, organizationsApi } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organization';
import type { User } from '@/types';

interface Role {
  _id: string;
  name: string;
  description?: string;
  permissions: string[];
  is_system: boolean;
  organization_id: { _id: string; name: string; slug: string } | null;
}

interface UserRole {
  _id: string;
  user_id: { _id: string };
  organization_id: { _id: string; name: string; slug: string } | null;
  role_id: Role;
  created_at: string;
}

interface Organization {
  _id: string;
  name: string;
  slug: string;
  status: string;
}

interface OrgAssignment {
  _id: string;
  organization_id: { _id: string; name: string; slug: string };
  role_id: { _id: string; name: string };
}

export default function UsersPage() {
  const { hasPermission } = useAuthStore();
  const selectedOrg = useOrganizationStore((s) => s.selectedOrg);
  const [loading, setLoading] = useState(true);

  // "Invite to current org" modal
  const [showInviteOrgModal, setShowInviteOrgModal] = useState(false);
  const [inviteOrgRoles, setInviteOrgRoles] = useState<Role[]>([]);
  // VIDA-293 Fase B: la invitación admite VARIOS roles. El backend solo guarda un
  // `pending_org_role`, así que el primero va como rol principal en la invitación
  // y el resto se añaden como asignaciones extra sobre el userId que devuelve.
  const [inviteOrgRoleIds, setInviteOrgRoleIds] = useState<string[]>([]);

  const toggleInviteOrgRole = (roleId: string) =>
    setInviteOrgRoleIds((prev) =>
      prev.includes(roleId) ? prev.filter((r) => r !== roleId) : [...prev, roleId]
    );
  const [inviteOrgForm, setInviteOrgForm] = useState({
    email: '',
    roleId: '',
    firstName: '',
    lastName: '',
  });
  const [invitingOrg, setInvitingOrg] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRolesModal, setShowRolesModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [resendingInvitation, setResendingInvitation] = useState<string | null>(null);
  const [sendingReset, setSendingReset] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    user_type: 'internal' as 'internal' | 'external',
  });

  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  // VIDA-293: por defecto el alta se asigna a la organización activa. Antes los
  // roles se creaban SIEMPRE globales y el usuario quedaba "suelto", sin
  // pertenecer a ninguna org.
  const [assignToActiveOrg, setAssignToActiveOrg] = useState(true);

  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    status: '',
    user_type: 'internal' as 'internal' | 'external',
  });

  const [newRoleId, setNewRoleId] = useState('');
  const [createRoles, setCreateRoles] = useState<Role[]>([]);

  // Organization management state
  const [userOrgs, setUserOrgs] = useState<OrgAssignment[]>([]);
  const [allOrgs, setAllOrgs] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedOrgRoleId, setSelectedOrgRoleId] = useState('');
  const [orgRoles, setOrgRoles] = useState<Role[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const response = await usersApi.list();
      setUsers(response.data.users || []);
    } catch {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadUserRoles = async (userId: string) => {
    setRolesLoading(true);
    try {
      const [rolesRes, allRolesRes] = await Promise.all([
        usersApi.getRoles(userId),
        rolesApi.list(),
      ]);
      setUserRoles(rolesRes.data.roles || []);
      setAvailableRoles(allRolesRes.data.roles || []);
    } catch (err) {
      console.error('Error loading roles:', err);
      toast.error('Error al cargar roles');
    } finally {
      setRolesLoading(false);
    }
  };

  const loadUserOrganizations = async (userId: string) => {
    try {
      const [userOrgsRes, allOrgsRes, rolesRes] = await Promise.all([
        usersApi.getOrganizations(userId),
        organizationsApi.list(1, 100),
        rolesApi.list(),
      ]);
      setUserOrgs(userOrgsRes.data.organizations || []);
      setAllOrgs(allOrgsRes.data.organizations || []);
      // Filter to org-assignable roles (org_admin, editor, viewer - not super_admin)
      const assignableRoles = (rolesRes.data.roles || []).filter(
        (r: Role) => !r.organization_id && r.name !== 'super_admin'
      );
      setOrgRoles(assignableRoles);
    } catch (err) {
      console.error('Error loading user organizations:', err);
      toast.error('Error al cargar organizaciones del usuario');
    }
  };

  const handleAddToOrg = async () => {
    if (!selectedUser || !selectedOrgId || !selectedOrgRoleId) return;
    try {
      await organizationsApi.addMember(selectedOrgId, selectedUser._id, selectedOrgRoleId);
      toast.success('Usuario añadido a la organización');
      await loadUserOrganizations(selectedUser._id);
      await loadUserRoles(selectedUser._id);
      setSelectedOrgId('');
      setSelectedOrgRoleId('');
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al añadir a organización';
      toast.error(message);
    }
  };

  const handleRemoveOrgRole = async (assignment: OrgAssignment) => {
    if (!selectedUser) return;
    // Granular delete: only this specific (user, org, role) row, NOT every role
    // the user has in that org. Allows a user with multiple roles in one org to
    // keep the others.
    try {
      await usersApi.removeRole(
        selectedUser._id,
        assignment.role_id._id,
        assignment.organization_id._id
      );
      toast.success('Rol eliminado');
      await loadUserOrganizations(selectedUser._id);
      await loadUserRoles(selectedUser._id);
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al eliminar rol';
      toast.error(message);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email) {
      toast.error('Email es requerido');
      return;
    }
    try {
      await usersApi.create({
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        roles: selectedRoles,
        user_type: formData.user_type,
        // Por defecto el alta pertenece a la org activa; desmarcando la casilla
        // se crea con roles globales (solo tiene sentido para super_admin).
        organization_id: assignToActiveOrg && selectedOrg ? selectedOrg._id : undefined,
      });
      toast.success(
        assignToActiveOrg && selectedOrg
          ? `Invitación enviada. Se asignará a ${selectedOrg.name}.`
          : 'Invitación enviada por email'
      );
      setShowCreateModal(false);
      setFormData({ email: '', first_name: '', last_name: '', user_type: 'internal' });
      setSelectedRoles([]);
      loadData();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al crear';
      toast.error(message);
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await usersApi.update(selectedUser._id, {
        user_type: editFormData.user_type,
        profile: {
          first_name: editFormData.first_name,
          last_name: editFormData.last_name,
        },
      });
      toast.success('Usuario actualizado');
      setShowEditModal(false);
      setSelectedUser(null);
      loadData();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
      toast.error(message);
    }
  };

  const handleResendInvitation = async (userId: string) => {
    setResendingInvitation(userId);
    try {
      await usersApi.resendInvitation(userId);
      toast.success('Invitación reenviada');
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
      toast.error(message);
    } finally {
      setResendingInvitation(null);
    }
  };

  // Fuerza el envío del email de recuperación (solo super admin). Se pide
  // confirmación porque invalida cualquier enlace anterior del usuario.
  const handleSendPasswordReset = async (user: User) => {
    const ok = window.confirm(
      `¿Enviar el email de recuperación de contraseña a ${user.email}?\n\n` +
        `Recibirá un enlace válido durante 60 minutos. Si tenía otro enlace pendiente, dejará de servir.`
    );
    if (!ok) return;

    setSendingReset(user._id);
    try {
      const res = await usersApi.sendPasswordReset(user._id);
      toast.success(res.data?.message || `Email enviado a ${user.email}`);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'No se pudo enviar el email';
      toast.error(message);
    } finally {
      setSendingReset(null);
    }
  };

  const openEditModal = async (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      first_name: user.profile?.first_name || '',
      last_name: user.profile?.last_name || '',
      status: user.status,
      user_type: user.user_type || 'internal',
    });
    await Promise.all([
      loadUserRoles(user._id),
      loadUserOrganizations(user._id),
    ]);
    setShowEditModal(true);
  };

  const openCreateModal = async () => {
    try {
      const response = await rolesApi.list();
      setCreateRoles(response.data.roles || []);
    } catch {
      toast.error('Error al cargar roles');
    }
    setShowCreateModal(true);
  };

  const openInviteOrgModal = async () => {
    if (!selectedOrg) {
      toast.error('Selecciona una organización primero');
      return;
    }
    try {
      const response = await rolesApi.list();
      // Roles asignables a esta org: globales (organization_id null) excepto
      // super_admin, mas los especificos de esta org. Reutilizamos el filtro
      // que ya usa el modal de roles.
      const list: Role[] = (response.data.roles || []).filter(
        (r: Role) =>
          (!r.organization_id || r.organization_id._id === selectedOrg._id) &&
          r.name !== 'super_admin'
      );
      setInviteOrgRoles(list);
      setInviteOrgForm({ email: '', roleId: '', firstName: '', lastName: '' });
      setInviteOrgRoleIds([]);
      setShowInviteOrgModal(true);
    } catch {
      toast.error('Error al cargar roles');
    }
  };

  const handleInviteOrg = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;
    if (!inviteOrgForm.email || inviteOrgRoleIds.length === 0) {
      toast.error('Email y al menos un rol son obligatorios');
      return;
    }
    setInvitingOrg(true);
    try {
      const [primaryRoleId, ...extraRoleIds] = inviteOrgRoleIds;
      const res = await organizationsApi.invite(selectedOrg._id, {
        email: inviteOrgForm.email,
        roleId: primaryRoleId,
        firstName: inviteOrgForm.firstName || undefined,
        lastName: inviteOrgForm.lastName || undefined,
      });

      // Roles adicionales: se asignan sobre el userId que devuelve la invitación
      // (vale para usuario nuevo, pendiente o ya activo). Si alguno falla, se
      // avisa pero la invitación ya está hecha.
      const newUserId = res.data.userId as string | undefined;
      if (newUserId && extraRoleIds.length > 0) {
        const failed: string[] = [];
        for (const roleId of extraRoleIds) {
          try {
            await usersApi.assignRole(newUserId, roleId, selectedOrg._id);
          } catch {
            failed.push(roleId);
          }
        }
        if (failed.length > 0) {
          toast.error(`${failed.length} rol(es) adicional(es) no se pudieron asignar`);
        }
      }

      // Backend devuelve un message localizado y un status discriminado por caso
      toast.success(res.data.message || 'Invitación enviada');
      setShowInviteOrgModal(false);
      setInviteOrgForm({ email: '', roleId: '', firstName: '', lastName: '' });
      setInviteOrgRoleIds([]);
      loadData();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error
        || 'Error al invitar';
      toast.error(message);
    } finally {
      setInvitingOrg(false);
    }
  };

  const toggleCreateRole = (roleId: string) => {
    setSelectedRoles(prev => 
      prev.includes(roleId) 
        ? prev.filter(id => id !== roleId)
        : [...prev, roleId]
    );
  };

  const openRolesModal = async (user: User) => {
    setSelectedUser(user);
    await Promise.all([
      loadUserRoles(user._id),
      loadUserOrganizations(user._id),
    ]);
    setShowRolesModal(true);
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !newRoleId) return;
    try {
      await usersApi.assignRole(selectedUser._id, newRoleId);
      toast.success('Rol asignado');
      await loadUserRoles(selectedUser._id);
      setNewRoleId('');
      loadData();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al asignar rol';
      toast.error(message);
    }
  };

  const handleRemoveRole = async (assignment: UserRole) => {
    if (!selectedUser) return;
    try {
      await usersApi.removeRole(
        selectedUser._id,
        assignment.role_id._id,
        assignment.organization_id?._id
      );
      toast.success('Rol eliminado');
      await loadUserRoles(selectedUser._id);
      loadData();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error al eliminar rol';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    setDeleteLoading(true);
    try {
      await usersApi.delete(selectedUser._id);
      toast.success('Usuario eliminado');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      loadData();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Error';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div>
      <Header
        title="Usuarios"
        subtitle="Gestiona los usuarios del sistema"
        actions={
          <div className="flex items-center gap-2">
            {/* Invitar a la org seleccionada en el sidebar — disponible para
                cualquier user con ORG_INVITE (incluye super_admin via *) */}
            {selectedOrg && hasPermission('org:invite') && (
              <button onClick={openInviteOrgModal} className="btn-secondary">
                <PlusIcon className="w-5 h-5 mr-2" />
                Invitar a {selectedOrg.name}
              </button>
            )}
            {hasPermission('users:create') && (
              <button onClick={openCreateModal} className="btn-primary">
                <PlusIcon className="w-5 h-5 mr-2" />
                Invitar usuario
              </button>
            )}
          </div>
        }
      />

      <div className="p-6">
        {users.length === 0 ? (
          <EmptyState
            title="No hay usuarios"
            description="Invita al primer usuario para empezar"
            action={
              hasPermission('users:create')
                ? { label: 'Invitar usuario', onClick: () => setShowCreateModal(true) }
                : undefined
            }
          />
        ) : (
          <div className="card">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Tipo</th>
                    <th>Roles</th>
                    <th>Estado</th>
                    <th>Último acceso</th>
                    <th>Creado</th>
                    <th className="text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-vida-100 rounded-full flex items-center justify-center">
                            <span className="text-vida-600 font-medium text-sm">
                              {user.profile?.first_name?.[0] || user.email[0].toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {user.profile?.first_name} {user.profile?.last_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="text-gray-500">{user.email}</td>
                      <td>
                        {user.user_type === 'external' ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                            Externo
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Interno</span>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => openRolesModal(user)}
                          className="text-vida-600 hover:text-vida-700 text-sm flex items-center gap-1"
                        >
                          <UserGroupIcon className="w-4 h-4" />
                          Ver roles
                        </button>
                      </td>
                      <td>
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(user.status)}`}>
                          {user.status === 'pending' ? 'Pendiente' : user.status}
                        </span>
                      </td>
                      <td className="text-gray-500">
                        {user.last_login ? new Date(user.last_login).toLocaleString() : 'Nunca'}
                      </td>
                      <td className="text-gray-500">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="flex items-center justify-end gap-2">
                          {user.status === 'pending' && hasPermission('users:update') && (
                            <button
                              onClick={() => handleResendInvitation(user._id)}
                              disabled={resendingInvitation === user._id}
                              className="p-1 text-gray-400 hover:text-vida-600"
                              title="Reenviar invitación"
                            >
                              {resendingInvitation === user._id ? (
                                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                              ) : (
                                <EnvelopeIcon className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          {/* Forzar envío del email de recuperación. Solo super
                              admin y solo en cuentas activas: en una cuenta
                              pendiente el enlace no sirve, ahí va la invitación. */}
                          {hasPermission('*') && user.status === 'active' && (
                            <button
                              onClick={() => handleSendPasswordReset(user)}
                              disabled={sendingReset === user._id}
                              className="p-1 text-gray-400 hover:text-vida-600 disabled:opacity-50"
                              title="Enviar email de recuperación de contraseña"
                            >
                              {sendingReset === user._id ? (
                                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                              ) : (
                                <KeyIcon className="w-5 h-5" />
                              )}
                            </button>
                          )}
                          {hasPermission('users:update') && (
                            <button
                              onClick={() => openEditModal(user)}
                              className="p-1 text-gray-400 hover:text-vida-600"
                              title="Editar"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                          )}
                          {hasPermission('users:delete') && (
                            <button
                              onClick={() => {
                                setSelectedUser(user);
                                setShowDeleteConfirm(true);
                              }}
                              className="p-1 text-gray-400 hover:text-red-600"
                              title="Eliminar"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)} title="Invitar usuario">
        <form onSubmit={handleCreate} className="space-y-4">
          <p className="text-sm text-gray-600">Se enviará una invitación por email.</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Nombre</label>
              <input
                type="text"
                className="input"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Apellido</label>
              <input
                type="text"
                className="input"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label">Email *</label>
            <input
              type="email"
              className="input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>
          <div>
            <label className="label">Tipo de usuario</label>
            <select
              className="input"
              value={formData.user_type}
              onChange={(e) => setFormData({ ...formData, user_type: e.target.value as 'internal' | 'external' })}
            >
              <option value="internal">Interno</option>
              <option value="external">Externo (Colaborador)</option>
            </select>
          </div>
          {createRoles.length > 0 && (
            <div>
              <label className="label">Roles</label>
              <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg divide-y">
                {createRoles.map((role) => (
                  <label key={role._id} className="flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role._id)}
                      onChange={() => toggleCreateRole(role._id)}
                      className="w-4 h-4 text-vida-600 rounded border-gray-300"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{role.name}</p>
                      <p className="text-xs text-gray-500">
                        {role.organization_id ? role.organization_id.name : 'Global'}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Ámbito del alta: por defecto la organización activa (VIDA-293) */}
          {selectedOrg && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={assignToActiveOrg}
                  onChange={(e) => setAssignToActiveOrg(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-vida-600 rounded border-gray-300"
                />
                <span className="text-sm text-gray-700">
                  Asignar a <strong>{selectedOrg.name}</strong>
                  <span className="block text-xs text-gray-500">
                    Los roles marcados se aplican dentro de esta organización. Si lo desmarcas, se
                    asignan como roles globales (el usuario no pertenecerá a ninguna organización).
                  </span>
                </span>
              </label>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowCreateModal(false);
                setSelectedRoles([]);
              }}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary">
              Enviar invitación
            </button>
          </div>
        </form>
      </Modal>

      {/* Invite user to current organization (existing or new) */}
      <Modal
        open={showInviteOrgModal}
        onClose={() => !invitingOrg && setShowInviteOrgModal(false)}
        title={selectedOrg ? `Invitar a ${selectedOrg.name}` : 'Invitar a la organización'}
      >
        <form onSubmit={handleInviteOrg} className="space-y-4">
          <p className="text-sm text-gray-500">
            Si el email pertenece a un usuario que ya existe, se le asignará el rol y recibirá
            un correo de aviso. Si es un email nuevo, se crea el usuario y se le envía la
            invitación para fijar contraseña.
          </p>
          <div>
            <label className="label">Email *</label>
            <input
              type="email"
              required
              className="input"
              value={inviteOrgForm.email}
              onChange={(e) => setInviteOrgForm({ ...inviteOrgForm, email: e.target.value })}
              placeholder="usuario@ejemplo.com"
              autoFocus
            />
          </div>
          <div>
            <label className="label">Roles * (puedes marcar varios)</label>
            <p className="mb-2 text-xs text-gray-500">
              Los permisos se <strong>suman</strong>: p. ej. "Productos" + "Ecommerce" mantiene el acceso a
              productos generales; solo "Ecommerce" lo deja fuera.
            </p>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
              {inviteOrgRoles.map((role) => (
                <label
                  key={role._id}
                  className="flex items-start gap-2 p-2 cursor-pointer hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={inviteOrgRoleIds.includes(role._id)}
                    onChange={() => toggleInviteOrgRole(role._id)}
                    className="mt-0.5 w-4 h-4 text-vida-600 rounded border-gray-300"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-gray-900">{role.name}</span>
                    {role.description && (
                      <span className="block text-xs text-gray-500">{role.description}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Nombre (opcional)</label>
              <input
                type="text"
                className="input"
                value={inviteOrgForm.firstName}
                onChange={(e) => setInviteOrgForm({ ...inviteOrgForm, firstName: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Apellido (opcional)</label>
              <input
                type="text"
                className="input"
                value={inviteOrgForm.lastName}
                onChange={(e) => setInviteOrgForm({ ...inviteOrgForm, lastName: e.target.value })}
              />
            </div>
          </div>
          <p className="text-xs text-gray-400">
            Si el usuario es nuevo, nombre y apellido se guardan en su perfil al aceptar.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setShowInviteOrgModal(false)}
              disabled={invitingOrg}
            >
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={invitingOrg}>
              {invitingOrg ? 'Enviando...' : 'Invitar'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={showEditModal} onClose={() => { setShowEditModal(false); setSelectedUser(null); }} title="Editar usuario">
        <form onSubmit={handleEdit} className="space-y-4">
          <div>
            <label className="label">Email</label>
            <input type="email" className="input bg-gray-100" value={selectedUser?.email || ''} disabled />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Nombre</label>
              <input
                type="text"
                className="input"
                value={editFormData.first_name}
                onChange={(e) => setEditFormData({ ...editFormData, first_name: e.target.value })}
              />
            </div>
            <div>
              <label className="label">Apellido</label>
              <input
                type="text"
                className="input"
                value={editFormData.last_name}
                onChange={(e) => setEditFormData({ ...editFormData, last_name: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label">Tipo de usuario</label>
            <select
              className="input"
              value={editFormData.user_type}
              onChange={(e) => setEditFormData({ ...editFormData, user_type: e.target.value as 'internal' | 'external' })}
            >
              <option value="internal">Interno</option>
              <option value="external">Externo (Colaborador)</option>
            </select>
          </div>
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h4 className="font-medium text-gray-900 mb-3">Roles asignados</h4>
            {userRoles.length === 0 ? (
              <p className="text-gray-500 text-sm">No tiene ningún rol asignado.</p>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {userRoles.map((assignment) => (
                  <div key={assignment._id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <ShieldCheckIcon className="w-4 h-4 text-vida-600" />
                      <span className="text-sm text-gray-900">{assignment.role_id.name}</span>
                      <span className="text-xs text-gray-500">
                        {assignment.organization_id ? `(${assignment.organization_id.name})` : '(Global)'}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveRole(assignment)}
                      className="p-1 text-gray-400 hover:text-red-600"
                      title="Quitar rol"
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <label className="label">Añadir rol</label>
            <div className="flex gap-2">
              <select
                value={newRoleId}
                onChange={(e) => setNewRoleId(e.target.value)}
                className="input flex-1"
              >
                <option value="">Seleccionar rol...</option>
                {availableRoles.filter(r => !userRoles.some(ur => ur.role_id._id === r._id)).map((role) => (
                  <option key={role._id} value={role._id}>
                    {role.name} {role.organization_id ? `(${role.organization_id.name})` : '(Global)'}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleAssignRole}
                disabled={!newRoleId}
                className="btn-primary"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" className="btn-secondary" onClick={() => { setShowEditModal(false); setSelectedUser(null); }}>
              Cerrar
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={showRolesModal} onClose={() => { setShowRolesModal(false); setSelectedUser(null); }} title="Gestionar roles y permisos">
        {rolesLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vida-600 mx-auto" />
          </div>
        ) : (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-gray-600 mb-3">
                Asigna roles a <strong>{selectedUser?.email}</strong> para definir sus permisos en el sistema.
              </p>

              <div className="flex gap-3">
                <select
                  value={newRoleId}
                  onChange={(e) => setNewRoleId(e.target.value)}
                  className="input flex-1"
                >
                  <option value="">Seleccionar rol...</option>
                  {availableRoles.map((role) => (
                    <option key={role._id} value={role._id}>
                      {role.name} {role.organization_id ? `(${role.organization_id.name})` : '(Global)'}
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleAssignRole}
                  disabled={!newRoleId}
                  className="btn-primary"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Roles asignados</h4>
              {userRoles.length === 0 ? (
                <p className="text-gray-500 text-sm">No tiene ningún rol asignado.</p>
              ) : (
                <div className="space-y-2">
                  {userRoles.map((assignment) => (
                    <div key={assignment._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <ShieldCheckIcon className="w-5 h-5 text-vida-600" />
                        <div>
                          <p className="font-medium text-gray-900">{assignment.role_id.name}</p>
                          <p className="text-xs text-gray-500">
                            {assignment.organization_id 
                              ? assignment.organization_id.name 
                              : 'Rol global'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveRole(assignment)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Quitar rol"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-3">Organizaciones</h4>
              {userOrgs.length === 0 ? (
                <p className="text-gray-500 text-sm mb-3">No pertenece a ninguna organización.</p>
              ) : (
                <div className="space-y-2 mb-3">
                  {userOrgs.map((assignment) => (
                    <div key={assignment._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <UserGroupIcon className="w-5 h-5 text-vida-600" />
                        <div>
                          <p className="font-medium text-gray-900">{assignment.organization_id.name}</p>
                          <p className="text-xs text-gray-500">
                            {assignment.role_id.name} &middot; {assignment.organization_id.slug}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveOrgRole(assignment)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Quitar este rol"
                      >
                        <XMarkIcon className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <select
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  className="input flex-1"
                >
                  <option value="">Seleccionar organización...</option>
                  {/* Todas las orgs activas. La misma org puede aparecer varias
                      veces en la lista de asignaciones si el usuario tiene
                      varios roles en ella; aqui solo bloqueamos el par exacto
                      (org, rol) ya asignado, no la org entera. */}
                  {allOrgs
                    .filter((org) => org.status === 'active')
                    .map((org) => (
                      <option key={org._id} value={org._id}>{org.name}</option>
                    ))}
                </select>
                <select
                  value={selectedOrgRoleId}
                  onChange={(e) => setSelectedOrgRoleId(e.target.value)}
                  className="input w-36"
                >
                  <option value="">Rol...</option>
                  {/* Roles cuya combinacion (org, role) ya existe se deshabilitan */}
                  {orgRoles.map((role) => {
                    const alreadyAssigned =
                      !!selectedOrgId &&
                      userOrgs.some(
                        (uo) =>
                          uo.organization_id._id === selectedOrgId &&
                          uo.role_id._id === role._id
                      );
                    return (
                      <option
                        key={role._id}
                        value={role._id}
                        disabled={alreadyAssigned}
                      >
                        {role.name}
                        {alreadyAssigned ? ' (ya asignado)' : ''}
                      </option>
                    );
                  })}
                </select>
                <button
                  onClick={handleAddToOrg}
                  disabled={!selectedOrgId || !selectedOrgRoleId}
                  className="btn-primary"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="font-medium text-gray-900 mb-2">Permisos del usuario</h4>
              <div className="flex flex-wrap gap-2">
                {Array.from(new Set(userRoles.flatMap(r => r.role_id.permissions))).sort().map((permission) => (
                  <span key={permission} className="px-2 py-1 bg-vida-100 text-vida-700 rounded text-xs font-mono">
                    {permission}
                  </span>
                ))}
                {userRoles.length === 0 && (
                  <p className="text-gray-500 text-sm">Sin permisos</p>
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setSelectedUser(null); }}
        onConfirm={handleDelete}
        title="Eliminar usuario"
        message={`¿Eliminar a "${selectedUser?.email}"?`}
        confirmText="Eliminar"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
}
